import {
  Breadcrumb,
  Collapse,
  Divider,
  Modal,
  Typography,
  message,
  Select,
} from "antd";
import { RxLoop } from "react-icons/rx";
import { MdOutlineArrowOutward, MdOutlineEdit } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaChevronDown } from "react-icons/fa";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useApiCall } from "../services/api";
import { Link, NavLink, useNavigate, useOutletContext } from "react-router-dom";
import SelectClientModal from "../components/models/SelectClient";
import ChangeAddress from "../components/models/ChangeAddress";
import { useGlobalModal } from "../context/SuccessModalProvider";
import ConfirmStatusModal from "../components/models/ConfirmStatusModal";
import { cartRefresh, triggerRefresh } from "../redux/auth/cartRefreshSlice";
import PrimaryButton from "../components/Forms/PrimaryButton";
import { formatAddress, handleRouteNav } from "../utils";
import { disclaimerItems, roleIds } from "../constant";
import {
  PiBuildingsFill,
  PiIdentificationCard,
  PiWalletFill,
} from "react-icons/pi";
import { setCounts } from "../redux/auth/navCountSlice";

const { Title } = Typography;

const CheckOut = () => {
  const { postApi, loading } = useApiCall();
  const [cartItems, setCartItems] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const {
    full_name,
    user_id,
    role_id,
    customer_id,
    is_customer,
    is_sales_manager,
    is_admin,
  } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [isSelectClientModalOpen, setIsSelectClientModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [defaultAddress, setDefaultAddress] = useState({
    deliveryAddress: "",
    billingAddress: "",
  });
  const [changeAddressModal, setChangeAddressModal] = useState(false);
  const [modalAddressType, setModalAddressType] = useState("delivery");

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [isOutOfStockModalVisible, setIsOutOfStockModalVisible] =
    useState(false);
  const [outOfStockUpdatedItems, setOutOfStockUpdatedItems] = useState([]);
  const [cartUpdatingLoader, setCartUpdatingLoader] = useState(false);

  const { showPopup } = useGlobalModal();

  const navigate = useNavigate();

  const fetchCartDetails = async () => {
    try {
      const response = await postApi(
        is_customer
          ? "cart/get-cart-customer-id"
          : "sales/cart/get-cart-user-id",
        is_customer ? { customer_id } : { user_id },
      );
      if (response?.status) {
        setCartItems(response?.data);
      } else setCartItems([]);
      dispatch(setCounts({ cartItems: response?.total_items || 0 }));
    } catch (error) {
      console.error("Error fetching cart details:", error);
    }
  };

  const fetchAllClients = async () => {
    try {
      const response = await postApi("sales/assign-customer-list", {
        user_id,
      });
      setAllClients(response.data || []);
    } catch (error) {
      console.error("Error fetching client details:", error);
    }
  };

  // once the address flow is complete, do this
  const fetchAddresses = async (customer) => {
    if (!customer?.customer_id) return;
    try {
      const urlEndPoint = is_customer ? "address" : "sales/address/list";
      const response = await postApi(urlEndPoint, {
        customer_id: customer.customer_id,
      });
      setAddresses(response.data || []);

      const defaultDeliveryAddr =
        response.data?.find((addr) => addr.is_default_delivery) || "";
      const defaultBillingAddr =
        response.data?.find((addr) => addr.is_default_billing) || "";

      if (defaultDeliveryAddr) {
        setDefaultAddress((prev) => ({
          ...prev,
          deliveryAddress: defaultDeliveryAddr,
        }));
      } else {
        const {
          address_line1,
          address_line2,
          city,
          state,
          post_code,
          country,
        } = customer;
        setDefaultAddress((prev) => ({
          ...prev,
          deliveryAddress: {
            address_line1,
            address_line2,
            city,
            state,
            post_code,
            country,
          },
        }));
      }
      if (defaultBillingAddr) {
        setDefaultAddress((prev) => ({
          ...prev,
          billingAddress: defaultBillingAddr,
        }));
      } else {
        const {
          address_line1,
          address_line2,
          city,
          state,
          post_code,
          country,
        } = customer;
        setDefaultAddress((prev) => ({
          ...prev,
          billingAddress: {
            address_line1,
            address_line2,
            city,
            state,
            post_code,
            country,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  const handleItemDelete = (item) => {
    setSelectedItem(item);
    setIsDeleteModalVisible(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setSelectedItem(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;

    try {
      const response = await postApi(
        is_customer ? "cart/delete-item" : "sales/cart/delete-item",
        { cart_id: selectedItem.cart_id },
        true,
        null,
        () => dispatch(triggerRefresh()),
      );
      if (response?.status) {
        fetchCartDetails();
      }
    } catch (error) {
      console.error("Error removing item from cart:", error);
    } finally {
      setIsDeleteModalVisible(false);
      setSelectedItem(null);
    }
  };

  useEffect(() => {
    fetchCartDetails();

    if (is_customer) {
      fetchAddresses({ customer_id: customer_id });
      setSelectedClient({ customer_id: customer_id });
    }
  }, []);

  const handleSubmit = async () => {
    const deliveryAddressString =
      formatAddress(defaultAddress?.deliveryAddress) ||
      formatAddress(selectedClient);
    const billingAddressString =
      formatAddress(defaultAddress?.billingAddress) ||
      formatAddress(selectedClient);

    const inStockItems = cartItems
      .filter((i) => Number(i.total_in_stock_quantity) ?? 0 > 0)
      .map((i) => ({
        ...i,
        in_stock: 1,
        cases: Number(i.cases_in_stock || "0"),
      }));

    const makeToOrderItems = cartItems
      .filter((i) => Number(i.total_make_to_order_quantity) ?? 0 > 0)
      .map((i) => ({
        ...i,
        in_stock: 0,
        cases: Number(i.cases_make_to_order || "0"),
        cases_id: "",
        cases_no: "",
      }));

    const finalItems = [...inStockItems, ...makeToOrderItems].map((i) => {
      delete i.updated_by;
      delete i.updated_by_id;
      return {
        ...i,
        quantity_meters:
          i.in_stock === 1
            ? i.total_in_stock_quantity
            : i.total_make_to_order_quantity,
      };
    });

    const body = {
      customer_id: selectedClient?.customer_id,
      delivery_address: deliveryAddressString,
      delivery_title: defaultAddress?.deliveryAddress?.title || "Home",
      billing_address: billingAddressString,
      billing_title: defaultAddress?.billingAddress?.title || "Home",
      user_id: is_customer ? 0 : user_id,
      assigned_user_id:
        is_sales_manager || is_admin ? selectedClient.user_id : user_id,
      order_items: finalItems,
      updated_by: full_name,
      updated_by_id: is_customer ? 0 : user_id,
      delivery_city:
        defaultAddress?.deliveryAddress?.city || selectedClient?.city,
      sales_name: selectedClient?.user?.full_name || "",
      party_code: selectedClient?.party_code,
    };

    try {
      const response = await postApi("order/add-order", body, true);
      if (response?.status) {
        fetchCartDetails();
        setSelectedClient(is_customer ? customer_id : null);
        dispatch(triggerRefresh());
      } else if (response?.data) {
        const backendStockMap = new Map(
          (response.data || []).map((item) => [String(item.cart_id), item]),
        );

        const setFixedDecimal = (num, decimals = 2) => {
          const parsed = Number(num);
          return parsed % 1 === 0 ? parsed : parsed.toFixed(decimals);
        };

        const updatedItemsWithCart = cartItems
          .map((cartItem) => {
            const backendItem = backendStockMap.get(String(cartItem.cart_id));
            const existingMakeToOrderCases = Number(
              cartItem.cases_make_to_order || 0,
            );
            const originalCases = String(cartItem.cases_no || "")
              .split(",")
              .map((caseNo) => caseNo.trim())
              .filter(Boolean);
            const originalCaseIds = String(cartItem.cases_id || "")
              .split(",")
              .map((caseId) => caseId.trim())
              .filter(Boolean);
            const inStockCases = backendItem?.cases
              ? String(backendItem.cases)
                  .split(",")
                  .map((caseNo) => caseNo.trim())
                  .filter(Boolean)
              : [];
            const inStockCaseSet = new Set(inStockCases);
            const outOfStockCases = originalCases.filter(
              (caseNo) => !inStockCaseSet.has(caseNo),
            );
            const updatedMakeToOrderCases =
              existingMakeToOrderCases + outOfStockCases.length;
            // const updatedInStockQuantity = Number(
            //   backendItem?.total_qty || 0,
            // ).toFixed(2);
            const updatedInStockQuantity = setFixedDecimal(
              backendItem?.total_qty || 0,
            );
            const updatedMakeToOrderQuantity =
              Number(cartItem.total_make_to_order_quantity || 0) +
              outOfStockCases.length * 250;

            const updatedCasesId = [];
            const updatedCasesNo = [];
            originalCases.forEach((caseNo, index) => {
              if (inStockCaseSet.has(caseNo)) {
                updatedCasesNo.push(caseNo);
                if (originalCaseIds[index]) {
                  updatedCasesId.push(originalCaseIds[index]);
                }
              }
            });

            return {
              ...cartItem,
              old_cases_in_stock: Number(cartItem.cases_in_stock || 0),
              old_total_in_stock_quantity: setFixedDecimal(
                cartItem.total_in_stock_quantity || 0,
              ),
              old_cases_make_to_order: Number(
                cartItem.cases_make_to_order || 0,
              ),
              old_total_make_to_order_quantity: setFixedDecimal(
                cartItem.total_make_to_order_quantity || 0,
              ),
              cases_no: updatedCasesNo.join(","),
              cases_id: updatedCasesId.join(","),
              cases_in_stock: String(inStockCases.length),
              total_in_stock_quantity: updatedInStockQuantity,
              cases_make_to_order: String(updatedMakeToOrderCases),
              total_make_to_order_quantity: updatedMakeToOrderQuantity,
              total_quantity:
                updatedInStockQuantity + updatedMakeToOrderQuantity,
              in_stock: inStockCases.length > 0 ? 1 : 0,
              quantity_meters: String(updatedInStockQuantity),
              stockCases: inStockCases,
              outOfStockCases,
            };
          })
          .filter(Boolean);

        console.log("Updated items with cart mapping:", updatedItemsWithCart);
        setOutOfStockUpdatedItems(updatedItemsWithCart);
        setIsOutOfStockModalVisible(true);
      }
    } catch (error) {
      console.log("Error submitting form:", error);
    }
  };

  const handleItemEdit = (item) => {
    navigate(handleRouteNav(`/product-details/${item.sort_no}`, role_id), {
      state: { cart_id: item.cart_id },
    });
  };

  const handleUpdateCartItems = async () => {
    try {
      setCartUpdatingLoader(true);
      const updateUrl = is_customer
        ? "cart/update-cart"
        : "sales/cart/update-cart";

      for (const cartItem of outOfStockUpdatedItems) {
        const payload = {
          ...cartItem,
          total_quantity: Number(cartItem.total_quantity || 0),
        };
        const res = await postApi(updateUrl, payload);
        if (!res?.status) {
          console.error(`Failed to update cart ${cartItem.cart_id}`);
        }
      }
      setIsOutOfStockModalVisible(false);
      fetchCartDetails();
      showPopup({
        title: null,
        content: (
          <span className="custom-modal-title">Cart items updated.</span>
        ),
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
    } catch (error) {
      console.error("Error updating cart items:", error);
    } finally {
      setCartUpdatingLoader(false);
    }
  };

  const handleCheckoutAddressSaved = (savedAddress) => {
    setDefaultAddress((prev) => {
      const next = { ...prev };

      if (prev?.deliveryAddress?.address_id === savedAddress?.address_id) {
        next.deliveryAddress = { ...prev.deliveryAddress, ...savedAddress };
      }

      if (prev?.billingAddress?.address_id === savedAddress?.address_id) {
        next.billingAddress = { ...prev.billingAddress, ...savedAddress };
      }

      return next;
    });
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Checkout");
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("", role_id) },
            {
              title: "Product Catalogue ",
              href: handleRouteNav("/product-catalog", role_id),
            },
            {
              title: "Checkout",
              href: handleRouteNav("/product-catalog/checkout", role_id),
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href}>{item.title}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton]);

  return (
    <>
      <>
        <div
          className={
            is_customer
              ? "flex flex-1 justify-between pb-6"
              : "flex justify-between"
          }
        >
          <div className="flex flex-1 max-w-[55%] w-[45%] flex-col gap-4 pb-6">
            {!is_customer && (
              <div className="bg-[#ffffff] w-[100%] p-6 flex flex-col gap-2 rounded-3xl">
                <div className="flex items-center justify-between gap-4 leading-8">
                  {/* Left section */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-[45px] h-[45px] bg-[#A334341A] flex justify-center items-center rounded-full">
                      <img
                        src={"/assets/icons/User.png"}
                        alt="card-logo"
                        className="w-6 h-6"
                      />
                    </div>
                    <p className="text-[#000000] font-semibold text-lg truncate min-w-0">
                      Client name
                    </p>
                  </div>

                  {/* Right section */}
                  <div className="flex items-center justify-end flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSelectClientModalOpen(true);
                      }}
                      className="flex items-center gap-2 text-[#A33434] cursor-pointer px-3 py-2 hover:bg-transparent min-w-0"
                    >
                      <span className="truncate max-w-[150px] block">
                        {selectedClient?.company_name || "Select Client"}
                      </span>
                      <svg
                        className="w-4 h-4 text-[#A33434]"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="border-2 rounded-2xl p-4 mt-2">
                  <p className="text-[#000] text-left font-bold text-base leading-8">
                    {selectedClient?.company_name || "Select Client"}
                  </p>
                </div>
              </div>
            )}
            {selectedClient && (
              <>
                <div className="bg-[#ffffff] w-[100%] p-6 flex flex-col gap-2 rounded-3xl">
                  <div className="flex flex-wrap gap-2 leading-8 justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-[45px] h-[45px] bg-[#A334341A] flex justify-center items-center rounded-full">
                        <PiBuildingsFill size={24} fill="#A33434" />
                      </div>
                      <p className="text-[#000000] font-semibold text-lg leading-3">
                        Deliver to
                      </p>
                    </div>
                    <PrimaryButton
                      text="Change Address"
                      icon={<RxLoop color="#fff" />}
                      onClick={() => {
                        setChangeAddressModal(true);
                        setModalAddressType("delivery");
                      }}
                      iconOnleft
                      // textClassName="text-[#A33434] font-medium text-base leading-3 "
                      // iconClassName="!h-[30px] !w-[30px]"
                      btnType="ghost"
                    />
                  </div>
                  <div className="border-2 rounded-2xl p-4 mt-2">
                    <p className="text-[#000] text-left text-base leading-6 flex flex-col gap-2">
                      <strong className="text-base">
                        {defaultAddress?.deliveryAddress?.title}
                      </strong>
                      {formatAddress(defaultAddress?.deliveryAddress) ||
                        formatAddress(selectedClient)}
                    </p>
                  </div>
                </div>
                <div className="bg-[#ffffff] w-[100%] p-6 flex flex-wrap flex-col gap-2 rounded-3xl">
                  <div className="flex flex-wrap gap-2 leading-8 justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-[45px] h-[45px] bg-[#A334341A] flex justify-center items-center rounded-full">
                        <PiWalletFill size={24} fill="#A33434" />
                      </div>
                      <p className="text-[#000000] font-semibold text-lg leading-3">
                        Bill to
                      </p>
                    </div>
                    <PrimaryButton
                      text="Change Address"
                      icon={<RxLoop color="#fff" />}
                      onClick={() => {
                        setChangeAddressModal(true);
                        setModalAddressType("billing");
                      }}
                      iconOnleft
                      // textClassName="text-[#A33434] font-medium text-base leading-3 "
                      // iconClassName="!h-[30px] !w-[30px]"
                      btnType="ghost"
                    />
                  </div>
                  <div className="border-2 rounded-2xl p-4 mt-2">
                    <p className="text-[#000] text-left text-base leading-6 flex flex-col gap-2">
                      <strong className="text-base">
                        {defaultAddress?.billingAddress?.title}
                      </strong>
                      {formatAddress(defaultAddress?.billingAddress) ||
                        formatAddress(selectedClient)}
                    </p>
                  </div>
                </div>
                <div className="bg-[#ffffff] w-[100%] px-6 py-4 flex flex-col gap-2 rounded-3xl">
                  <div className="flex leading-8 justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-[30px] h-[30px] min-w-[30px] min-h-[30px] bg-[#A334341A] flex justify-center items-center rounded-full">
                        <img
                          src={"/assets/icons/TruckTrailer.png"}
                          alt="card-logo"
                          className="w-[20px] h-[20px]"
                        />
                      </div>
                      <p className="text-[#000000] leading-5 text-start">
                        <strong>Estimated Dispatch</strong> - Piece-dyed 30-40
                        days, Top-dyed 45-60 days.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
            <div>
              <PrimaryButton
                className="mx-auto"
                disabled={!selectedClient || !cartItems.length}
                text="Place Order"
                onClick={handleSubmit}
                loading={loading}
              />
            </div>

            <div className=" pt-6 rounded-lg mt-8">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">
                  Disclaimers
                </h2>
              </div>

              <div className="space-y-4">
                {disclaimerItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1.5">
                      <div className="w-2 h-2 bg-[#A33434] rounded-full"></div>
                    </div>
                    <p className="flex-1 text-left text-gray-600 leading-relaxed text-sm">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Divider
            type="vertical"
            className="mx-6 border-l-2 border-gray-300 self-stretch h-auto mb-[10%]"
          />

          <div className="flex max-w-[400px] w-[45%] flex-col gap-4 pb-6">
            {cartItems.length > 0 ? (
              cartItems.map((item) => {
                return (
                  <div
                    className="bg-[#ffffff] w-[100%] px-4 py-2 rounded-3xl"
                    key={item.cart_id}
                  >
                    <Collapse
                      defaultActiveKey={["1"]}
                      ghost
                      expandIconPosition="end"
                      expandIcon={({ isActive }) => (
                        <FaChevronDown
                          className="transition-transform duration-300 mt-2"
                          style={{
                            transform: isActive
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          }}
                        />
                      )}
                      items={[
                        {
                          key: "1",
                          label: (
                            <div className="w-full rounded-2xl">
                              <div className="flex items-center gap-4">
                                <div className="w-[50px] h-[50px] bg-[#A334341A] flex justify-center items-center rounded-full">
                                  <img
                                    src={item.product_picture_url}
                                    alt={item.short_name}
                                    className="w-[50px] h-[50px]"
                                  />
                                </div>
                                <div className="flex flex-col gap-1 justify-start text-start">
                                  <p className="text-xl font-bold break-words whitespace-normal">
                                    {item.short_name}
                                  </p>
                                  {/* <p className="font-semibold text-gray-500 text-sm">
                                    ₹{Number(item.price_per_meter || 0).toLocaleString("en-IN")} /
                                    meter
                                  </p> */}
                                </div>
                              </div>
                            </div>
                          ),
                          children: (
                            <>
                              <div className="w-full rounded-2xl">
                                {/* Details Grid */}
                                <div className="grid grid-cols-2 font-medium leading-6 gap-4 mb-4">
                                  <div className="text-left space-y-1">
                                    <p className="text-sm text-gray-500">
                                      Dyed Type
                                    </p>
                                    <p className="text-base leading-6 font-medium text-gray-900 capitalize">
                                      {item.dye_type}
                                    </p>
                                  </div>
                                  <div className="space-y-1 text-right">
                                    <p className="text-sm text-gray-500">
                                      Shade
                                    </p>
                                    <p className="text-base leading-6 font-medium text-gray-900">
                                      {item.shade_name}
                                    </p>
                                  </div>
                                  {Number(item.cases_in_stock) > 0 && (
                                    <div className="text-left leading-6 space-y-1">
                                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                                        In Stock
                                        <span className="block mr-1 h-1.5 w-1.5 rounded-full bg-[#009F10] ring-2 ring-white"></span>
                                      </div>
                                      <p className="text-base leading-6 font-medium text-gray-900">
                                        {item.cases_in_stock || 0} cases (
                                        {item.total_in_stock_quantity || 0} m)
                                      </p>
                                    </div>
                                  )}
                                  {Number(item.cases_make_to_order) > 0 && (
                                    <div
                                      className={`${
                                        Number(item.cases_in_stock) > 0
                                          ? "text-right"
                                          : "text-left"
                                      } leading-6 space-y-1`}
                                    >
                                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                                        Make to order
                                        <span className="block h-1.5 w-1.5 rounded-full bg-[#FFB32E] ring-2 ring-white"></span>
                                      </div>
                                      <p className="text-base leading-6 font-medium text-gray-900">
                                        {item.cases_make_to_order || 0} cases (
                                        {item.total_make_to_order_quantity || 0}{" "}
                                        m)
                                      </p>
                                    </div>
                                  )}
                                </div>

                                {/* Comments Section */}
                                <div className=" text-left font-medium leading-6 mb-6 space-y-1">
                                  <p className="text-sm leading-6 text-gray-500">
                                    Comments
                                  </p>
                                  <p className="text-base leading-6 text-gray-900">
                                    {item.comments || "-"}
                                  </p>
                                </div>
                                <div>
                                  <Divider className="my-6" />
                                </div>
                                <div className="flex justify-center items-center">
                                  <PrimaryButton
                                    text="Edit"
                                    icon={<MdOutlineEdit color="#fff" />}
                                    onClick={() => handleItemEdit(item)}
                                    iconOnleft
                                    btnType="ghost"
                                  />
                                  <Divider
                                    type="vertical"
                                    className="mx-6 h-12"
                                  />
                                  <PrimaryButton
                                    text="Delete"
                                    icon={<RiDeleteBin6Line color="#fff" />}
                                    onClick={() => handleItemDelete(item)}
                                    iconOnleft
                                    btnType="ghost"
                                  />
                                </div>
                              </div>
                            </>
                          ),
                        },
                      ]}
                    />
                  </div>
                );
              })
            ) : (
              <>
                {is_customer && (
                  <div className="flex flex-col justify-center gap-10">
                    <div className="flex justify-center">
                      <img
                        src="/assets/icons/emptyCart.svg"
                        className="w-fit "
                      ></img>
                    </div>
                    <div>
                      <span className="text-[#000] text-md font-bold">
                        Your cart is empty!
                      </span>
                      <div>
                        <span className="text-[#000] text-md">
                          Looks like you haven't made a choice yet.
                        </span>
                      </div>
                    </div>

                    <div>
                      <NavLink
                        to="/product-catalog"
                        className="underline text-[#a33434] text-xl"
                      >
                        Go to Catalogue
                      </NavLink>
                    </div>
                  </div>
                )}

                {!is_customer && (
                  <div className="bg-[#fff] p-6 rounded-[32px] text-center">
                    <p className="text-2xl font-bold text-[#A33434]">
                      No items in cart
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </>
      <SelectClientModal
        isOpen={isSelectClientModalOpen}
        setIsOpen={setIsSelectClientModalOpen}
        title={"Select Client"}
        clients={allClients}
        selectedClient={selectedClient}
        onSubmit={(client) => {
          setSelectedClient(client);
          setIsSelectClientModalOpen(false);
          fetchAddresses(client);
        }}
        loading={loading}
      />

      <ChangeAddress
        isMainModalOpen={changeAddressModal}
        setIsMainModalOpen={setChangeAddressModal}
        addresses={addresses}
        setAddresses={setAddresses}
        customer_id={selectedClient?.customer_id}
        addressType={modalAddressType}
        defaultAddress={defaultAddress}
        setDefaultAddress={setDefaultAddress}
        onAddressSaved={handleCheckoutAddressSaved}
      />
      <ConfirmStatusModal
        open={isDeleteModalVisible}
        onClose={handleCancelDelete}
        handleSubmit={handleConfirmDelete}
        title="Delete Item"
        subTitle="Do you want to delete this item?"
        buttonTitle="Delete Item"
        loading={loading}
      />

      <Modal
        className="flex flex-col items-center justify-center form-css-5 full-width-modal"
        open={isOutOfStockModalVisible}
        footer={null}
        onCancel={() => setIsOutOfStockModalVisible(false)}
        width={600}
      >
        <Title className="input-css font-abhaya" level={2}>
          Important Information!
        </Title>
        <span className=" text-gray-500">
          The following items were automatically updated because some cases were
          not found in stock.
        </span>

        {/* <div className="flex gap-3 justify-center items-center mt-4">
          <div className="w-[40px] h-[40px] bg-[#A334341A] rounded-full flex items-center justify-center">
            <img
              src="/assets/icons/TruckTrailer.png"
              alt="Cube"
              className="w-auto h-[20px] object-contain"
            />
          </div>

          <div className="flex flex-col text-start">
            <span className="">Out-of-stock cases were moved to make-to-order automatically.</span>
          </div>
        </div> */}

        <div className="w-full mt-5">
          {outOfStockUpdatedItems.length > 0 ? (
            outOfStockUpdatedItems
              .filter(
                (item) =>
                  Number(item.old_cases_in_stock) !==
                  Number(item.cases_in_stock),
              )
              .map((item, idx) => (
                <div key={idx} className="w-full rounded-2xl mt-5 border p-3">
                  <div className="flex items-center gap-4">
                    <div className="w-[50px] h-[50px] bg-[#A334341A] flex justify-center items-center rounded-full">
                      <img
                        src={item.product_picture_url}
                        alt={item.short_name}
                        className="w-[50px] h-[50px]"
                      />
                    </div>
                    <div className="flex flex-col justify-start text-start">
                      <p className="text-xl font-bold break-words whitespace-normal">
                        {item.short_name || `Cart ${item.cart_id}`}
                      </p>
                      <p className="font-semibold text-gray-500 text-lg">
                        {item.shade_name}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 font-medium leading-6 gap-4 my-3">
                    <div className="text-left leading-6 space-y-1">
                      <>
                        <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                          In Stock (OLD)
                          <span className="block mr-1 h-1.5 w-1.5 rounded-full bg-[#009F10] ring-2 ring-white"></span>
                        </div>

                        <p className="text-base leading-6 font-medium text-gray-900">
                          {item.old_cases_in_stock || 0} cases (
                          {item.old_total_in_stock_quantity || 0} m)
                        </p>
                      </>
                    </div>
                    <div className="text-right leading-6 space-y-1">
                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                        Make to order
                        <span className="block h-1.5 w-1.5 rounded-full bg-[#FFB32E] ring-2 ring-white"></span>
                      </div>
                      <p className="text-base leading-6 font-medium text-gray-900">
                        {item.old_cases_make_to_order || 0} cases (
                        {item.old_total_make_to_order_quantity || 0} m)
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 font-medium leading-6 gap-4 my-3">
                    <div className="text-left leading-6 space-y-1">
                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                        In Stock (NEW)
                        <span className="block mr-1 h-1.5 w-1.5 rounded-full bg-[#009F10] ring-2 ring-white"></span>
                      </div>
                      <p className="text-base leading-6 font-medium text-gray-900">
                        {item.cases_in_stock || 0} cases (
                        {item.total_in_stock_quantity || 0} m)
                      </p>
                    </div>
                    <div className="text-right leading-6 space-y-1">
                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                        Make to order
                        <span className="block h-1.5 w-1.5 rounded-full bg-[#FFB32E] ring-2 ring-white"></span>
                      </div>
                      <p className="text-base leading-6 font-medium text-gray-900">
                        {item.cases_make_to_order || 0} cases (
                        {item.total_make_to_order_quantity || 0} m)
                      </p>
                    </div>
                  </div>
                </div>
              ))
          ) : (
            <p className="text-gray-600 text-center">No updated items</p>
          )}
        </div>
        <div className="flex gap-3 mt-6 w-full justify-center">
          <PrimaryButton
            text="Update cart"
            btnType="primary"
            onClick={handleUpdateCartItems}
            loading={loading || cartUpdatingLoader}
            className="me-[30px]"
          />
        </div>
      </Modal>
    </>
  );
};

export default CheckOut;
